import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Theater, MapPin, Globe, IdCard, Users, Calendar,
  ArrowRight, BadgeCheck, Loader2, Save, Ruler, ExternalLink, Share2, Quote,
} from "lucide-react";
import OrgTeamPanel from "@/components/OrgTeamPanel";

export default function TheaterHallDashboard() {
  const [org, setOrg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [bookings, setBookings] = useState({ count: 0, counts: {} });
  const [stageCatalog, setStageCatalog] = useState([]);

  const accent = "#7C3AED";

  useEffect(() => {
    api.get("/onboard/me/organization")
      .then(({ data }) => setOrg(data.organization))
      .catch(() => setOrg(null));
    api.get("/meta/theater-hall-stage-types")
      .then(({ data }) => setStageCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
    api.get("/theater-hall/bookings/mine")
      .then(({ data }) => setBookings(data || { count: 0, counts: {} }))
      .catch(() => {});
  }, []);

  if (!org) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55"
           data-testid="theater-hall-loading">Loading dashboard…</div>
    );
  }

  const stageLabel = stageCatalog.find((s) => s.key === org.theater_hall_stage_type)?.label
                      || org.theater_hall_stage_type || "—";
  const requestedCount = bookings.counts?.requested || 0;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="theater-hall-dashboard">
      {/* HERO */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        L18 · Theater Hall
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
          style={{ color: accent }}
          data-testid="theater-hall-brand">
        {org.brand_name || org.legal_name}
      </h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        {org.legal_name && org.legal_name !== org.brand_name && (
          <span>{org.legal_name}</span>
        )}
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
            <BadgeCheck size={10} className="inline mr-1"/> Verified
          </span>
        )}
        {org.registered_city && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
            <MapPin size={10} className="inline mr-1"/> {org.registered_city}
          </span>
        )}
        <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase"
              style={{ background: `${accent}22`, color: accent }}
              data-testid="theater-hall-stage-chip">
          {stageLabel}
        </span>
        <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65"
              data-testid="theater-hall-seats-chip">
          {org.theater_hall_seats || "?"} seats
        </span>
      </div>

      {/* PUBLIC PROFILE ACTIONS */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="theater-hall-profile-actions">
        {org.public_handle && (
          <>
            <Link to={`/theater-hall/${org.public_handle}`} target="_blank"
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white transition"
                  data-testid="theater-hall-view-public">
              <ExternalLink size={11}/> View Public Profile
            </Link>
            <button onClick={() => {
                const url = `${window.location.origin}/theater-hall/${org.public_handle}`;
                navigator.clipboard.writeText(url).then(() => toast.success("Public link copied."));
              }}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white transition"
              data-testid="theater-hall-copy-link">
              <Share2 size={11}/> Copy Link
            </button>
          </>
        )}
      </div>

      {/* FACILITY CARD */}
      <div className="mt-8 cs-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">Facility Details</div>
            <div className="font-display text-lg uppercase tracking-tight mt-1">Stage · Tech · Rates</div>
          </div>
          <Button className="h-9 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white"
                  onClick={() => setEditing(true)}
                  data-testid="theater-hall-edit-btn">
            Edit
          </Button>
        </div>
        <div className="grid sm:grid-cols-4 gap-4 text-[13px]">
          <FacilityStat label="Width" value={`${org.theater_hall_stage_width || 0} m`} icon={Ruler}/>
          <FacilityStat label="Depth" value={`${org.theater_hall_stage_depth || 0} m`} icon={Ruler}/>
          <FacilityStat label="Height" value={`${org.theater_hall_stage_height || 0} m`} icon={Ruler}/>
          <FacilityStat label="Seats" value={org.theater_hall_seats || 0} icon={Users}/>
        </div>
        {org.theater_hall_tech_specs && (
          <div className="mt-4 text-[12px] text-white/65 leading-relaxed"
               data-testid="theater-hall-tech-specs">
            {org.theater_hall_tech_specs}
          </div>
        )}
        {org.theater_hall_rate_card && (
          <div className="mt-4 pt-4 border-t border-white/8 grid sm:grid-cols-3 gap-4 text-[12px]">
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Evening slot</div>
              <div className="mt-1 text-white font-semibold">₹{(org.theater_hall_rate_card.evening || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Matinee slot</div>
              <div className="mt-1 text-white font-semibold">₹{(org.theater_hall_rate_card.matinee || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">Currency</div>
              <div className="mt-1 text-white font-semibold">{org.theater_hall_rate_card.currency || "INR"}</div>
            </div>
          </div>
        )}
      </div>

      {/* TILE GRID */}
      <div className="mt-12">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-3">Your Desks</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theater-hall-tiles">
          <Tile to="/theater-hall/bookings" TileIcon={Calendar}
                title="Booking Requests"
                desc={requestedCount > 0
                  ? `${requestedCount} new request${requestedCount === 1 ? "" : "s"} awaiting your response.`
                  : "Groups requesting to perform at your venue — accept, counter, or decline."}
                accent={accent}
                pulse={requestedCount > 0}/>
          <Tile to="/directory/orgs?type=theater_group" TileIcon={Users}
                title="Theater Groups"
                desc="Browse repertory ensembles and troupes — invite them for a residency."
                accent={accent}/>
          <Tile to="/directory?talent_subtype=variety_performer" TileIcon={Users}
                title="Variety Artists"
                desc="Book comics, musicians, magicians for one-nighters."
                accent={accent}/>
          <Tile to="/office/public-profile" TileIcon={Globe}
                title="Public Profile"
                desc="Your venue's page — photos, tech-rider, calendar, rates."
                accent={accent}/>
          <Tile to="/icard" TileIcon={IdCard}
                title="Owner iCard"
                desc="Your verified, co-branded identity card."
                accent={accent}/>
        </div>
      </div>

      <div className="mt-10">
        <OrgTeamPanel org={org}/>
      </div>

      {editing && (
        <EditHallModal
          org={org}
          stageCatalog={stageCatalog}
          onClose={() => setEditing(false)}
          onSaved={(fresh) => { setEditing(false); setOrg(fresh); }}
        />
      )}
    </div>
  );
}

function FacilityStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-white/45">
        <Icon size={10} /> {label}
      </div>
      <div className="mt-1 text-white font-semibold">{value}</div>
    </div>
  );
}

function Tile({ to, TileIcon, title, desc, accent, pulse }) {
  return (
    <Link to={to} className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group relative"
          data-testid={`theater-hall-tile-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      {pulse && (
        <span className="absolute top-3 right-3 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400/60"/>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400"/>
        </span>
      )}
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
           style={{ background: `${accent}22`, color: accent }}>
        <TileIcon size={18}/>
      </div>
      <div className="mt-4">
        <div className="font-display text-lg uppercase tracking-tight">{title}</div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">{desc}</p>
      </div>
      <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
          Open →
        </span>
        <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition"/>
      </div>
    </Link>
  );
}

function EditHallModal({ org, stageCatalog, onClose, onSaved }) {
  const [form, setForm] = useState({
    stage_type:   org.theater_hall_stage_type || "proscenium",
    seats:        org.theater_hall_seats || 0,
    stage_width:  org.theater_hall_stage_width || 0,
    stage_depth:  org.theater_hall_stage_depth || 0,
    stage_height: org.theater_hall_stage_height || 0,
    tech_specs:   org.theater_hall_tech_specs || "",
    evening_rate: org.theater_hall_rate_card?.evening || 0,
    matinee_rate: org.theater_hall_rate_card?.matinee || 0,
    bio:          org.public_bio || "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch("/theater-hall/mine", {
        stage_type: form.stage_type,
        seats: Number(form.seats) || 0,
        stage_width: Number(form.stage_width) || 0,
        stage_depth: Number(form.stage_depth) || 0,
        stage_height: Number(form.stage_height) || 0,
        tech_specs: form.tech_specs,
        bio: form.bio,
        rate_card: {
          evening: Number(form.evening_rate) || 0,
          matinee: Number(form.matinee_rate) || 0,
          currency: "INR",
        },
      });
      const { data } = await api.get("/onboard/me/organization");
      toast.success("Facility details updated.");
      onSaved(data.organization);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="theater-hall-edit-modal">
      <div className="w-full max-w-2xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-violet-500/15 text-violet-400 flex items-center justify-center">
            <Theater size={18}/>
          </div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">Edit Facility</div>
            <div className="text-[11px] text-white/55">Stage · tech · rates</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Stage Type">
            <select value={form.stage_type} onChange={set("stage_type")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="theater-hall-edit-stage-type">
              {stageCatalog.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
            </select>
          </Field>
          <Field label="Seats">
            <Input type="number" min={0} value={form.seats} onChange={set("seats")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-hall-edit-seats"/>
          </Field>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <Field label="Width (m)">
            <Input type="number" step="0.5" min={0} value={form.stage_width} onChange={set("stage_width")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-hall-edit-width"/>
          </Field>
          <Field label="Depth (m)">
            <Input type="number" step="0.5" min={0} value={form.stage_depth} onChange={set("stage_depth")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-hall-edit-depth"/>
          </Field>
          <Field label="Height (m)">
            <Input type="number" step="0.5" min={0} value={form.stage_height} onChange={set("stage_height")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-hall-edit-height"/>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Tech Specs">
            <Textarea value={form.tech_specs} onChange={set("tech_specs")} rows={4}
                      className="bg-black/40 border-white/15 min-h-[90px]"
                      data-testid="theater-hall-edit-tech-specs"/>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="About this venue (shown on public profile)">
            <Textarea value={form.bio} onChange={set("bio")} rows={4} maxLength={4000}
                      placeholder="History, location perks, what makes this hall special…"
                      className="bg-black/40 border-white/15 min-h-[90px]"
                      data-testid="theater-hall-edit-bio"/>
          </Field>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Field label="Evening rate (₹)">
            <Input type="number" min={0} value={form.evening_rate} onChange={set("evening_rate")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-hall-edit-evening-rate"/>
          </Field>
          <Field label="Matinee rate (₹)">
            <Input type="number" min={0} value={form.matinee_rate} onChange={set("matinee_rate")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-hall-edit-matinee-rate"/>
          </Field>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost"
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                  onClick={onClose}
                  data-testid="theater-hall-edit-cancel">Cancel</Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="theater-hall-edit-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : <><Save size={13} className="mr-2"/> Save</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
